import { ReactComponent as IconExclamationCircle } from '@/assets/svgs/icon-exclamation-circle.svg';
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg';
import Input from '@/components/Input/index';
import RadioButton from '@/components/RadioButton';
import toastType from '@/constants/toast-type/index';
import ETR_API from "@/services/api/etr";
import { validateEmail } from "@/utils/validate-email";
import classname from 'classnames';
import { isAddress } from "ethers/lib/utils";
import { filter, uniqueId } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useDid from '@/hooks/use-did/index';
import Search from '@/componentsDS/Search/index';
import { feature, features } from "@/utils/feature-verifier";

const WalletDropdown = ({
	addressBook,
	referenceRef,
	fetchEtr,
	addToastMessage,
	selected,
	setSelected,
	nominatedBeneficiary,
	title
}) => {
	const [searchTimeout, setSearchTimeout] = useState(null);

	const [data, setData] = useState(addressBook)

	const [searchText, setSearchText] = useState('');
	const [displayedSearchText, setDisplayedSearchText] = useState('');
	const [customError, setCustomErrorMessage] = useState(false);
	const [nominateError, setNominateErrorMessage] = useState(false);
	const { toFindOrg } = useDid();

	const { register, formState: { errors, isValid }, getValues, reset, watch } = useForm({
		mode: 'onChange'
	})

	const handleSearch = (value) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		setSearchTimeout(
			setTimeout(() => {
				setSearchText(value);
			}, 500)
		);
	};

	useEffect(() => {
		if (addressBook?.length)
			setData(addressBook)
	}, [addressBook])

	useEffect(() => {
		const filterData = filter(addressBook, item => {
			const lowerSearchText = searchText.toLowerCase()
			const lowerName = item?.name?.toLowerCase()
			const lowerEmail = item?.email?.toLowerCase()
			const lowerWalletAddress = item?.walletAddress?.toLowerCase()

			return (
				lowerName.includes(lowerSearchText) ||
				lowerEmail.includes(lowerSearchText) ||
				lowerWalletAddress.includes(lowerSearchText)
			)
		})
		setData(filterData)

		if (filterData.length === 0) {
			setCustomErrorMessage(true)
		}
	}, [searchText]);

	function checkDuplicate(name, key) {
		const duplicate = addressBook.find(address => address[key].toLowerCase() === name.toLowerCase())
		return Boolean(duplicate)
	}

	const ShowError = () => {
		if (errors.name?.message) {
			return (
				<div className="etr__update-wallet__dropdown__row etr__update-wallet__dropdown__row--error">
					<IconExclamationCircle /> <span>{errors.name.message}</span>
				</div>
			);
		}

		if (errors.wallet?.message) {
			return (
				<div className="etr__update-wallet__dropdown__row etr__update-wallet__dropdown__row--error">
					<IconExclamationCircle /> <span>{errors.wallet.message}</span>
				</div>
			);
		}

		if (errors.email?.message) {
			return (
				<div className="etr__update-wallet__dropdown__row etr__update-wallet__dropdown__row--error">
					<IconExclamationCircle /> <span>{errors.email.message}</span>
				</div>
			);
		}

		return null;
	};

	async function formHandler() {
		if (!isValid) return

		const payload = {
			orgName: getValues().name,
			walletAddress: getValues().wallet,
			email: getValues().email.toLowerCase(),
			pin: false
		}

		const [error] = await ETR_API.savedAddress(payload);

		if (error) {
			return addToastMessage(error, toastType.error)
		}

		addToastMessage('Address added successfully', toastType.success)

		fetchEtr()
		reset()
		setSelected(null);
	}

	const saveButtonClassname = classname({
		'etr__update-wallet__dropdown__list__col': true,
		'etr__update-wallet__dropdown__list__col--save': isValid,
		'etr__update-wallet__dropdown__list__col--disabled': !isValid
	})

	return (
		<div className="etr__update-wallet__dropdown__component" ref={referenceRef}>
			<div className='etr__update-wallet__dropdown__component__search'>
				{feature(features.NEW_DESIGN_SYSTEM) ? (
						<div style={{paddingBottom: '4px'}}>
							<Search
								placeholder="Search saved addresses"
								value={displayedSearchText}
								onChange={(e) => {
									setDisplayedSearchText(e.target.value)
									handleSearch(e.target.value)
								}}
								onCancel={() => {
									setDisplayedSearchText('')
									handleSearch('')
								}}
								variant='filled'
							/>
						</div>
					) : (
						<>
							<Input
								prefix={<IconSearch />}
								placeholder="Search saved addresses"
								addClass="etr__update-wallet__dropdown__search__input"
								value={displayedSearchText}
								onChange={(e) => {
									setDisplayedSearchText(e.target.value);
									handleSearch(e.target.value)
								}}
							/>
							{customError && (
								<div className="etr__update-wallet__dropdown__search__error">
									<IconExclamationCircle /> <span>No Match Found</span>
								</div>
							)}
							{nominateError && (
								<div className="etr__update-wallet__dropdown__search__error">
									<IconExclamationCircle /> <span>You have already nominated to this address</span>
								</div>
							)}
						</>
					)}
			</div>

			<hr />
			<div className='etr__update-wallet__dropdown__component__list'>
				<form className='etr__update-wallet__dropdown__row'>
					<div className='etr__update-wallet__dropdown__list__col'
						onClick={() => {
							setSelected({ id: "custom" })
						}}
					>
						<RadioButton
							check={selected?.id === "custom"}
						/>
					</div>
					<div className='etr__update-wallet__dropdown__list__col'
						onClick={() => {
							setSelected({ id: "custom" })
						}}
					>
						<Input
							addClass="etr__update-wallet__dropdown__input"
							placeholder="Organisation Name"
							name="name"
							defaultValue={data?.name || null}
							register={register({
								required: "Field cannot be left empty",
								validate: (v) => {
									if (!data?.name || v.toLowerCase() !== data.name.toLowerCase()) {
										const duplicateOrgName = checkDuplicate(v, "name");
										if (duplicateOrgName) {
											return "This organisation already exists";
										}
									}
									return true;
								}
							})}
							error={errors.name}
						/>
					</div>
					<div className='etr__update-wallet__dropdown__list__col'
						onClick={() => {
							setSelected({ id: "custom" })
						}}
					>
						<Input
							addClass="etr__update-wallet__dropdown__input"
							placeholder="Wallet Address"
							name="wallet"
							defaultValue={data?.walletAddress || null}
							register={register({
								required: "Field cannot be left empty",
								validate: (v) => {
									if (!data?.walletAddress || v.toLowerCase() !== data.walletAddress.toLowerCase()) {
										const duplicateWalletAddress = checkDuplicate(v, "walletAddress");
										if (duplicateWalletAddress) {
											return "This wallet address already exists";
										}

										const validWalletAddress = isAddress(v);
										if (!validWalletAddress) {
											return "Invalid address format";
										}

										const isPitstopWalletAddress = toFindOrg(v);
										if (isPitstopWalletAddress) {
											return "You can only add a Pitstop wallet address via the Admin Portal. Please approach your administrator."
										}
									}
									return true;
								},
							})}
							error={errors.wallet}
						/>
					</div>
					<div className='etr__update-wallet__dropdown__list__col'
						onClick={() => {
							setSelected({ id: "custom" })
						}}
					>
						<Input
							addClass="etr__update-wallet__dropdown__input"
							placeholder="Email"
							name="email"
							defaultValue={data?.email || null}
							register={register({
								required: "Field cannot be left empty",
								validate: (v) => {
									if (!data?.email || v.toLowerCase() !== data.email.toLowerCase()) {
										const validEmailAddress = validateEmail(v);

										if (!validEmailAddress) {
											return "Invalid email format";
										}
									}
									return true;
								},
							})}
							error={errors.email}
						/>
					</div>
					<div className={saveButtonClassname}
						onClick={() => {
							formHandler().catch(error => {
								console.error('Failed to handle form:', error);
							});
						}}
					>
						Save
					</div>
				</form>

				<ShowError />

				{data.map((item) => {
					const id = uniqueId()

					return (
						<div className='etr__update-wallet__dropdown__row'
							key={id}
							onClick={() => {
								setNominateErrorMessage(null)
								if (title === 'Owner' && nominatedBeneficiary === item.walletAddress) {
									setSelected(null)
									return setNominateErrorMessage(true)
								}
								setSelected(item)
							}}
						>
							<div className='etr__update-wallet__dropdown__list__col'>
								<RadioButton
									check={item.id === selected?.id}
								/>
							</div>
							<div className='etr__update-wallet__dropdown__list__col etr__update-wallet__dropdown__list__col--black '>
								<div className='etr__update-wallet__dropdown__list__col__text'>
									{item.name}
								</div>
							</div>
							<div className='etr__update-wallet__dropdown__list__col'>
								<div className='etr__update-wallet__dropdown__list__col__text'>
									{item.walletAddress}
								</div>
							</div>
							<div className='etr__update-wallet__dropdown__list__col'>
								<div className='etr__update-wallet__dropdown__list__col__text'>
									{item.email}
								</div>
							</div>
							<div className='etr__update-wallet__dropdown__list__col'></div>
						</div>
					)
				})}

			</div>
		</div>
	)
}

export default WalletDropdown
